"use client";

import { useState, type ReactNode } from "react";
import { Alert } from "./alert";
import { Badge } from "./badge";
import { Textfeld } from "./formular/textfeld";
import { Knopf } from "./knopf";

/** Mengeneingabe im Korb: 1 bis 99 (Kasse, Betreiber 19.10.2026 „Mengenangabe direkt“); was darüber hinaus gebucht werden darf, prüft der Server. */
export const KORB_MENGE_EINGABE_MAX = 99;

/**
 * Korb-Bausteine (Kantine Warenkorb, Musterkatalog M-107, Betreiber 19.10.2026 „Empfehlung“ und „T-B ok, Sperre ok“; Design-Guide
 * `korb.md`). EIN Baustein für die Kasse (Fenster „Verkauf“, mit `kunde`-Slot) und das Terminal (feste rechte Spalte), damit beide
 * dieselbe Optik tragen. Rein darstellend: Positionen, Summen, Zustand und Rechtefrage gehören dem Aufrufer (Anbieten = Erlauben, r110).
 *
 *  - `KorbKarte`: Summenkarte nach `compact-order-summary-card-with-promo-code` (Kopf „Korb (n Positionen)“, Körper, Fuß mit
 *    Hauptknopf). Slot `kunde` oben (Kasse: Kundenwahl), `meldung` über dem Knopf (Satz am Feld), `sperrHinweis` unter dem Knopf.
 *  - `KorbZeile`: Position mit Name, Preis, Allergen-Chips, Mengenwahl − und + (Touch, 44 px) und „Entfernen“. Ist die Position
 *    nicht bestellbar (`satz`), steht der Satz in der Warn-Variante (`Alert` `warnung`), die Mengenwahl entfällt, der Knopf „Entfernen“
 *    bleibt. „Bezahlen“ ist gesperrt, solange der Aufrufer `gesperrt` setzt (eine Position nicht bestellbar, Guthaben nicht gedeckt …),
 *    und ein Satz sagt warum (`sperrHinweis`, keine stille Sperre, r262).
 *
 * Allergene: `allergene` fehlt (Snack) = keine Zeile; leere Liste = „keine Angaben hinterlegt“ (heißt nie „frei“, LMIV Art. 44);
 * sonst ein neutrales `Badge` je Allergen. Es werden keine Allergien einer Person gespeichert oder gewarnt (Idee 6).
 *
 * Abweichung AW-120: Eigenbau aus Preline-Klassen (Summenkarte nach dem Katalogblock, Positionszeile nach
 * `editable-order-line-item-row` ohne Bild, Mengenwahl aus `Knopf` Größe `touch`), kein Katalogblock 1:1; siehe
 * `docs/ui-preline/abweichungen.json` und `docs/ui-preline/designguide/korb.md`.
 */

export function KorbZeile({
  name,
  preis,
  allergene,
  menge,
  mengeMax,
  beiMenge,
  beiEntfernen,
  satz,
  laedt = false,
  markiert = false,
  beiMarkieren,
}: {
  /** Position ist markiert (Kasse: Entf entfernt sie); zeigt einen sichtbaren Rahmen. */
  markiert?: boolean;
  /** Fokus oder Klick auf der Zeile: der Aufrufer merkt sich die markierte Position. */
  beiMarkieren?: () => void;
  name: string;
  /** Zeilensumme als fertiger Text („4,20 €“). */
  preis: string;
  /** Allergene (Anzeigenamen) der Position; `undefined` = Artikel ohne Allergenangabe (Snack), `[]` = keine Angaben hinterlegt. */
  allergene?: string[];
  menge: number;
  mengeMax: number;
  /** Neue Menge; 0 = Position entfernen (das „−“ bei Menge 1 entfernt). */
  beiMenge: (menge: number) => void;
  beiEntfernen: () => void;
  /** Position nicht bestellbar: der Satz (Ablehnung, r105), statt Mengenwahl. */
  satz?: string;
  /** Ein Vorgang läuft: Bedienelemente gesperrt. */
  laedt?: boolean;
}) {
  const nichtBestellbar = satz !== undefined;
  const [entwurf, setEntwurf] = useState<string | null>(null);
  // Übernimmt die getippte Menge: nur ganze Zahlen von 1 bis 99; alles andere springt auf die bisherige Menge zurück.
  const uebernehmen = () => {
    if (entwurf === null) return;
    const n = Number(entwurf);
    setEntwurf(null);
    if (entwurf.trim() !== "" && Number.isInteger(n) && n >= 1 && n <= KORB_MENGE_EINGABE_MAX && n !== menge) beiMenge(n);
  };
  return (
    <>
      {/* Preline: ecommerce/ecommerce-sub-orders-commerce-utilities/ecommerce-cat-cart-and-order-line-items/editable-order-line-item-row */}
      <li
        className={markiert ? "py-4 border-b last:border-b-0 border-line-2 first:pt-0 rounded-lg ring-2 ring-primary-focus" : "py-4 border-b last:border-b-0 border-line-2 first:pt-0"}
        data-markiert={markiert ? "ja" : undefined}
        onFocus={beiMarkieren}
        onClick={beiMarkieren}
      >
        <div className="flex items-start justify-between gap-x-3">
          <p className={nichtBestellbar ? "text-sm text-muted-foreground-1 line-through" : "text-sm text-foreground"}>
            {menge > 1 && !nichtBestellbar ? `${menge} × ` : ""}
            {name}
          </p>
          <p className={nichtBestellbar ? "shrink-0 text-sm tabular-nums text-muted-foreground-1" : "shrink-0 text-sm tabular-nums text-foreground"}>{preis}</p>
        </div>
        {allergene !== undefined && (
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            {allergene.length > 0 ? (
              allergene.map((a) => (
                <Badge key={a} ton="neutral">
                  {a}
                </Badge>
              ))
            ) : (
              <span className="py-0.5 px-2 inline-flex items-center rounded-full text-xs border border-dashed border-line-2 text-muted-foreground-1">keine Angaben hinterlegt</span>
            )}
          </div>
        )}
        {nichtBestellbar ? (
          <div className="mt-3 space-y-3">
            <Alert ton="warnung">{satz}</Alert>
            <Knopf groesse="touch" onClick={beiEntfernen} disabled={laedt}>
              Entfernen
            </Knopf>
          </div>
        ) : (
          <div className="mt-3 flex items-center gap-x-2">
            <Knopf
              nurSymbol
              groesse="touch"
              label={menge <= 1 ? `${name} entfernen` : `${name}: eine Portion weniger`}
              symbol={
                <svg className="shrink-0 size-4" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M5 12h14" />
                </svg>
              }
              onClick={() => beiMenge(menge - 1)}
              disabled={laedt}
            />
            <div className="w-20">
              <Textfeld
                art="number"
                label={`Menge ${name}`}
                labelVersteckt
                groesse="kompakt"
                inputMode="numeric"
                min={1}
                max={KORB_MENGE_EINGABE_MAX}
                step={1}
                value={entwurf ?? String(menge)}
                disabled={laedt}
                data-korb-menge=""
                onChange={(e) => setEntwurf(e.target.value)}
                onBlur={uebernehmen}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    uebernehmen();
                    e.currentTarget.blur();
                  } else if (e.key === "Escape") {
                    e.preventDefault();
                    setEntwurf(null);
                    e.currentTarget.blur();
                  }
                }}
              />
            </div>
            <Knopf
              nurSymbol
              groesse="touch"
              label={`${name}: eine Portion mehr`}
              symbol={
                <svg className="shrink-0 size-4" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M5 12h14" />
                  <path d="M12 5v14" />
                </svg>
              }
              onClick={() => beiMenge(menge + 1)}
              disabled={laedt || menge >= mengeMax}
            />
          </div>
        )}
      </li>
    </>
  );
}

export function KorbKarte({
  anzahl,
  kunde,
  children,
  leerText,
  summe,
  zahlhinweis,
  meldung,
  bezahlenText,
  beiBezahlen,
  gesperrt,
  sperrHinweis,
  tastenHinweis,
  laedt = false,
  fenster = false,
  fuss,
}: {
  /**
   * Fensterhöhe (Kasse, Betreiber 19.10.2026 „Kaufen-Knopf immer sichtbar“): die Karte füllt die Höhe ihres Behälters (`h-full`) und
   * gliedert sich in Kopf mit `kunde` (fest), Positionsliste (scrollt eigenständig) und Fuß (immer sichtbar: `fuss`, Summe, Hinweis, Knopf).
   * Der Behälter muss eine Höhe vorgeben (ab `lg`). Ohne `fenster` bleibt die Karte so hoch wie ihr Inhalt (Terminal).
   */
  fenster?: boolean;
  /** Nur mit `fenster`: Zeilen im festen Fuß über der Summe (Kasse: Zahlart und Wechselgeld). */
  fuss?: ReactNode;
  /** Kürzel am Knopf als Satz (Kasse: „Enter bezahlt, Entf entfernt die markierte Position.“); es gibt keinen `kbd`-Baustein. */
  tastenHinweis?: string;
  /** Anzahl der Positionen (Kopf „Korb (3 Positionen)“). */
  anzahl: number;
  /** Slot oberhalb der Positionen: an der Kasse die Kundenwahl; am Terminal leer. */
  kunde?: ReactNode;
  /** Die `KorbZeile`n. */
  children?: ReactNode;
  /** Text des leeren Korbs („Noch nichts im Korb.“). */
  leerText: string;
  /** Summe als fertiger Text („5,80 €“). */
  summe: string;
  /** Wie bezahlt wird („Wird vom Guthaben abgebucht.“, „Bar kassieren.“). */
  zahlhinweis: string;
  /** Satz am Feld über dem Knopf (Ablehnung oder Fehlschlag des Vorgangs), vom Aufrufer als `Alert` gesetzt. */
  meldung?: ReactNode;
  /** Beschriftung des Hauptknopfs („Bezahlen 5,80 €“). */
  bezahlenText: string;
  beiBezahlen: () => void;
  /** Bezahlen ist gesperrt (leerer Korb, eine Position nicht bestellbar, Kunde fehlt …): dann sagt `sperrHinweis`, warum. */
  gesperrt: boolean;
  sperrHinweis?: string;
  laedt?: boolean;
}) {
  if (fenster) {
    return (
      <>
        {/* Preline: ecommerce/ecommerce-sub-orders-commerce-utilities/ecommerce-cat-order-summary-cards/compact-order-summary-card-with-promo-code (Fensterhöhe: Kopf fest, Liste scrollt, Fuß fest) */}
        <section className="flex flex-col h-full min-h-0 overflow-y-auto bg-card border border-card-line shadow-2xs rounded-xl" aria-label="Korb" data-korb-fenster="">
          <div className="shrink-0 py-3 px-5 border-b border-card-header">
            <h2 className="inline-block font-semibold text-foreground">{`Korb (${anzahl} ${anzahl === 1 ? "Position" : "Positionen"})`}</h2>
          </div>
          {kunde && (
            <div className="shrink-0 pt-4 pb-4 px-5 border-b border-line-2" data-korb-kopf="">
              {kunde}
            </div>
          )}
          <div className="grow min-h-32 overflow-y-auto px-5 pt-4 pb-2" data-korb-liste="">
            {anzahl === 0 ? <p className="text-sm text-muted-foreground-1">{leerText}</p> : <ul>{children}</ul>}
          </div>
          <div className="sticky bottom-0 shrink-0 bg-card rounded-b-xl border-t border-card-footer" data-korb-fuss="">
            <div className="pt-4 px-5 space-y-3">
              {fuss}
              <dl className="grid grid-cols-2 gap-2">
                <dt className="font-semibold text-foreground">Summe</dt>
                <dd className="text-end font-semibold text-foreground tabular-nums">{summe}</dd>
              </dl>
              <p className="text-sm text-muted-foreground-1">{zahlhinweis}</p>
            </div>
            <div className="py-4 px-5 space-y-3">
              {meldung}
              <Knopf variante="primaer" groesse="touch" volleBreite="immer" onClick={beiBezahlen} disabled={gesperrt} laedt={laedt}>
                {bezahlenText}
              </Knopf>
              {gesperrt && sperrHinweis && <p className="text-xs text-muted-foreground-1">{sperrHinweis}</p>}
              {tastenHinweis && <p className="text-xs text-muted-foreground-1">{tastenHinweis}</p>}
            </div>
          </div>
        </section>
      </>
    );
  }
  return (
    <>
      {/* Preline: ecommerce/ecommerce-sub-orders-commerce-utilities/ecommerce-cat-order-summary-cards/compact-order-summary-card-with-promo-code */}
      <section className="flex flex-col bg-card border border-card-line shadow-2xs rounded-xl" aria-label="Korb">
        <div className="py-3 px-5 border-b border-card-header">
          <h2 className="inline-block font-semibold text-foreground">{`Korb (${anzahl} ${anzahl === 1 ? "Position" : "Positionen"})`}</h2>
        </div>
        <div className="pt-4 pb-5 px-5 flex flex-col">
          {kunde && <div className="pb-4 mb-4 border-b border-line-2">{kunde}</div>}
          {anzahl === 0 ? <p className="text-sm text-muted-foreground-1">{leerText}</p> : <ul>{children}</ul>}
          <div className="pt-4 mt-4 border-t border-line-2">
            <dl className="grid grid-cols-2 gap-2">
              <dt className="font-semibold text-foreground">Summe</dt>
              <dd className="text-end font-semibold text-foreground tabular-nums">{summe}</dd>
            </dl>
            <p className="mt-3 text-sm text-muted-foreground-1">{zahlhinweis}</p>
          </div>
        </div>
        <div className="py-4 px-5 border-t border-card-footer space-y-3">
          {meldung}
          <Knopf variante="primaer" groesse="touch" volleBreite="immer" onClick={beiBezahlen} disabled={gesperrt} laedt={laedt}>
            {bezahlenText}
          </Knopf>
          {gesperrt && sperrHinweis && <p className="text-xs text-muted-foreground-1">{sperrHinweis}</p>}
          {tastenHinweis && <p className="text-xs text-muted-foreground-1">{tastenHinweis}</p>}
        </div>
      </section>
    </>
  );
}
